import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

const CartContext = createContext(null);
const STORAGE_KEY = 'zk_cart';

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem(STORAGE_KEY) : null;
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch { /* ignore */ }
  }, [items]);

  const add = (product, qty = 1) => {
    const maxQty = Math.max(1, parseInt(product.max_order_qty, 10) || 5);
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        const target = Math.min(maxQty, next[idx].quantity + qty);
        if (target === next[idx].quantity) {
          toast.error(`Max ${maxQty} units allowed for ${product.name}`);
          return prev;
        }
        next[idx] = { ...next[idx], quantity: target, max_order_qty: maxQty };
        return next;
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          price: product.price,
          mrp: product.mrp,
          image_url: product.image_url,
          unit: product.unit,
          quantity: Math.min(maxQty, qty),
          max_order_qty: maxQty,
          vendor_id: product.vendor_id || null,
          vendor_name: product.vendor_name || '',
          vendor_slug: product.vendor_slug || '',
        },
      ];
    });
    toast.success(`${product.name} added to cart`);
  };

  const increment = (id) => setItems((prev) => prev.map((p) => {
    if (p.id !== id) return p;
    const max = Math.max(1, parseInt(p.max_order_qty, 10) || 5);
    if (p.quantity >= max) {
      toast.error(`Max ${max} units allowed for ${p.name}`);
      return p;
    }
    return { ...p, quantity: p.quantity + 1 };
  }));
  const decrement = (id) => setItems((prev) => prev.flatMap((p) => p.id === id ? (p.quantity > 1 ? [{ ...p, quantity: p.quantity - 1 }] : []) : [p]));
  const remove = (id) => setItems((prev) => prev.filter((p) => p.id !== id));
  const clear = () => setItems([]);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);
  const qtyOf = (id) => items.find((p) => p.id === id)?.quantity || 0;

  return (
    <CartContext.Provider value={{ items, add, increment, decrement, remove, clear, subtotal, count, qtyOf, cartOpen, setCartOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
